import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { ArrowLeft, Lock, User } from "lucide-react";
import { formStateFor } from "@/modules/identity/commands/authCopy";
import {
  checkPhone,
  formatLebanesePhone,
} from "@/modules/identity/surfaces";
import { Banner, Button, Field, StepIndicator } from "@/design/ui";
import { AuthShell } from "~/components/AuthShell";
import { BrowseAsGuest, FootLink, SubmitButton } from "~/components/forms";
import { post } from "~/lib/api";
import { useForm } from "~/lib/useForm";

/**
 * SignUpScreen (docs/ux/screens/SignUpScreen.md), consumer sign-up (H22).
 *
 * **Staged: identity first, then the password.** Splitting it keeps each step
 * to a glance and catches a mistake in a name before anybody picks a password.
 *
 * **Both steps live in one form and submit together**, and step 1 stays mounted
 * while hidden rather than being unmounted. That is design-system §12's trap
 * written down: unmounting a step throws away what was typed in it, and this
 * form posts everything at once because the backend creates the account and
 * *then* asks for the OTP.
 *
 * **Return moves to the next field.** Left to the browser, Enter in any field
 * submits the whole form, which from step 1 posts an account with no password.
 * Instead it walks the visible fields in order, runs Continue from the last
 * one on step 1, and only submits from the last field of step 2.
 */
function checkName(value: string, label: "First" | "Last"): string | undefined {
  if (!value.trim()) return `${label} name is required`;
  if (value.trim().length > 100) return `${label} name is too long`;
  return undefined;
}

interface SignUpResult {
  phone: string;
  verificationRequired: boolean;
}

export function SignUpScreen() {
  const navigate = useNavigate();
  const { state, pending, submit } = useForm();
  const [step, setStep] = useState<1 | 2>(1);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [localErrors, setLocalErrors] = useState<{
    firstName?: string;
    lastName?: string;
    phone?: string;
  }>({});

  // A server rejection on a step-1 field has to be visible, so go back to it.
  const serverStep1Error =
    state.fieldErrors?.firstName ??
    state.fieldErrors?.lastName ??
    state.fieldErrors?.phone;
  const [lastServerError, setLastServerError] = useState(serverStep1Error);
  if (serverStep1Error !== lastServerError) {
    setLastServerError(serverStep1Error);
    if (serverStep1Error) setStep(1);
  }

  function goToStep2() {
    const errors = {
      firstName: checkName(firstName, "First"),
      lastName: checkName(lastName, "Last"),
      phone: checkPhone(phone),
    };
    setLocalErrors(errors);
    if (!errors.firstName && !errors.lastName && !errors.phone) setStep(2);
  }

  // Whoever reaches step 2 is about to type a password, so put them in it.
  // Step 1 needs no equivalent: it is where the screen starts.
  useEffect(() => {
    if (step === 2) document.getElementById("password")?.focus();
  }, [step]);

  function onFieldKeyDown(event: React.KeyboardEvent<HTMLFormElement>) {
    const field = event.target;
    if (event.key !== "Enter" || event.nativeEvent.isComposing) return;
    if (!(field instanceof HTMLInputElement)) return;
    // The hidden step's inputs are still in the form (step 1 stays mounted),
    // so only those currently laid out count as "the next field".
    const visible = [...event.currentTarget.querySelectorAll("input")].filter(
      (input) => input.offsetParent !== null,
    );
    const next = visible[visible.indexOf(field) + 1];
    if (next) {
      event.preventDefault();
      next.focus();
    } else if (step === 1) {
      event.preventDefault();
      goToStep2();
    }
    // The last field of step 2 falls through to the form's own submit.
  }

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    const values = { firstName, lastName, phone };
    const created = await submit<SignUpResult>(
      () =>
        post("/auth/signup", {
          firstName,
          lastName,
          phone,
          password,
          confirmPassword,
        }),
      formStateFor,
      values,
    );
    // No session on purpose: the number is proved before a first login, so the
    // only way on from here is the code we just sent.
    if (created) {
      void navigate(`/verify?phone=${encodeURIComponent(created.phone)}`);
    }
  }

  return (
    <AuthShell>
      <div className="mb-6">
        <StepIndicator current={step} total={2} />
        <h1 className="mt-4 text-2xl font-extrabold text-ink">
          {step === 1 ? "Create your account" : "Pick a password"}
        </h1>
        <p className="mt-1 text-sm text-muted">
          {step === 1
            ? "We use your phone number to confirm it's you"
            : "Last step, then your account is ready"}
        </p>
      </div>

      {state.formError && <Banner>{state.formError}</Banner>}

      <form onSubmit={onSubmit} onKeyDown={onFieldKeyDown}>
        <fieldset disabled={pending} className="disabled:opacity-70">
          {/* Step 1 stays mounted when hidden so its values survive step 2. */}
          <div className={step === 1 ? undefined : "hidden"}>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field
                label="First name"
                name="firstName"
                value={firstName}
                onChange={setFirstName}
                error={localErrors.firstName ?? state.fieldErrors?.firstName}
                autoComplete="given-name"
                enterKeyHint="next"
                icon={User}
              />
              <Field
                label="Last name"
                name="lastName"
                value={lastName}
                onChange={setLastName}
                error={localErrors.lastName ?? state.fieldErrors?.lastName}
                autoComplete="family-name"
                enterKeyHint="next"
                icon={User}
              />
            </div>
            <Field
              label="Phone number"
              name="phone"
              type="tel"
              inputMode="tel"
              placeholder="71 234 456"
              prefix="+961"
              value={phone}
              onChange={(v) => setPhone(formatLebanesePhone(v))}
              error={localErrors.phone ?? state.fieldErrors?.phone}
              autoComplete="tel"
              enterKeyHint="next"
            />
            <Button onClick={goToStep2} className="w-full">
              Continue
            </Button>
          </div>

          <div className={step === 2 ? undefined : "hidden"}>
            <Field
              label="Password"
              name="password"
              type="password"
              value={password}
              onChange={setPassword}
              help="At least 8 characters"
              error={state.fieldErrors?.password}
              autoComplete="new-password"
              enterKeyHint="next"
              icon={Lock}
            />
            <Field
              label="Confirm password"
              name="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={setConfirmPassword}
              error={state.fieldErrors?.confirmPassword}
              autoComplete="new-password"
              enterKeyHint="done"
              icon={Lock}
            />
            <SubmitButton pending={pending} pendingLabel="Creating…">
              Create account
            </SubmitButton>
            <button
              type="button"
              onClick={() => setStep(1)}
              className="mt-3 flex w-full items-center justify-center gap-1.5 text-sm font-semibold text-muted transition-colors hover:text-body"
            >
              <ArrowLeft className="size-4" aria-hidden />
              Back to your details
            </button>
          </div>
        </fieldset>
      </form>

      <FootLink lead="Already have an account?" to="/login" className="mt-6">
        Log in instead
      </FootLink>
      <FootLink lead="Own a clinic or practice?" to="/join">
        Register your business
      </FootLink>
      <BrowseAsGuest />
    </AuthShell>
  );
}
